import type { Building } from "building-types/building/buildingTypes";
import getTargetedOpeningSettings from "../elements/openings/getTargetedOpeningSettings";
import {
  makeBuildingGableEndWallFraming,
  type MakeBuildingGableEndWallFramingParams,
} from "../elements/walls/makeBuildingGableWallFraming";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

export function select({
  levels,
  roofSystems,
  walls,
  planarGraphPerimeters,
  settings,
  settingsSets,
  optionChoices,
  windowPlacements,
  doorPlacements,
  blocks,
  enabledFeatures,
  doc: { settings: docSettings, entitySettings, headerAssemblies, framingComponents },
}: Pick<
  Building,
  | "levels"
  | "roofSystems"
  | "walls"
  | "planarGraphPerimeters"
  | "settings"
  | "settingsSets"
  | "optionChoices"
  | "windowPlacements"
  | "doorPlacements"
  | "blocks"
  | "enabledFeatures"
  | "doc"
>) {
  return {
    levels,
    walls,
    planarGraphPerimeters,
    roofSystems,
    blocks,
    wallSettings: settings.wall,
    wallCatalogSettings: settings.wallCatalog,
    wallFramingSettings: settings.wallFraming,
    headerFramingSettings: settings.headerFraming,
    docSettings,
    entitySettings,
    headerAssemblies,
    framingComponents,
    settingsSets,
    optionChoices,
    windowPlacements,
    doorPlacements,
    settings,
    wallPlanes: enabledFeatures.wallPlanes ?? false,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  wallPlanes: "shallow",
});

function generate(params: ReturnType<typeof select>): Pick<Building, "gableEndWallFramingSystems"> {
  const {
    levels,
    roofSystems,
    blocks,
    wallSettings,
    walls,
    planarGraphPerimeters,
    wallCatalogSettings,
    wallFramingSettings,
    headerFramingSettings,
    docSettings,
    entitySettings,
    headerAssemblies,
    framingComponents,
    settingsSets,
    optionChoices,
    windowPlacements,
    doorPlacements,
    settings,
    wallPlanes,
  } = params;

  if (wallPlanes) {
    return {
      gableEndWallFramingSystems: {},
    };
  }

  const targetedOpeningSettings = getTargetedOpeningSettings(
    {
      settingsSets,
      optionChoices,
      windowPlacements,
      doorPlacements,
      settings,
    },
    { settings: docSettings, entitySettings },
  );

  const makeBuildingGableWallFramingParams: MakeBuildingGableEndWallFramingParams = {
    roofSystems,
    blocks,
    wallSettings,
    levels,
    walls,
    planarGraphPerimeters,
    wallFramingSettings,
    wallCatalogSettings,
    targetedOpeningSettings,
    headerFramingSettings,
    headerAssemblies,
    framingComponents,
  };

  const gableEndWallFramingSystems = makeBuildingGableEndWallFraming(makeBuildingGableWallFramingParams);

  return {
    gableEndWallFramingSystems,
  };
}

export default memo(select, generate, compare);
